import { ArrowLeft, Calendar, FileText, MapPin, Stethoscope, User } from 'lucide-react';
import React from 'react';
import { useGoBack } from '~/shared/routing/useGoBack';
import { useRequiredParam } from '~/shared/routing/useRequiredParam';
import { useGetCertificationPeriodReviewOptions } from './queries';
import { DeadlineText, PriorityBadge, StatusBadge } from './PlanOfCareReviewTable';
import { ReferralTab } from './tabs/ReferralTab';
import { DemographicsTab } from './tabs/DemographicsTab';
import { SuspenseWrapper } from '~/shared/SuspenseWrapper';
import { OrdersTab } from './tabs/OrdersTab';
import { useSuspenseQuery } from '@tanstack/react-query';
import { OasisTab } from './tabs/oasis/OasisTab';
import { Tabs } from '~/shared/components/tabs/Tabs';
import { Callout } from '~/shared/components/Callout';
import { DiagnosesTab } from './tabs/DiagnosesTab';
import { LocatorItemsTab } from './tabs/LocatorItemsTab';
import { PlanOfCareDischargePlans } from '~/features/discharge/PlanOfCareDischargePlans';
import { ReviewTab } from './tabs/ReviewTab';

const SECTIONS = [
	{ id: 'referral', label: 'Referral' },
	{ id: 'demographics', label: 'Demographics' },
	{ id: 'orders', label: 'Orders' },
	{ id: 'frequencies', label: 'Frequencies' },
	{ id: 'goals', label: 'Goals & Interventions' },
	{ id: 'oasis', label: 'OASIS' },
	{ id: 'dx', label: 'Dx' },
	{ id: 'locator-items', label: 'Locator Items' },
	{ id: 'discharge-plans', label: 'Discharge Plans' },
	{ id: 'qa-review', label: 'QA Review' }
];

export function PlanOfCareReview() {
	const reviewId = useRequiredParam('reviewId');
	const goBack = useGoBack();
	const { data: review } = useSuspenseQuery(useGetCertificationPeriodReviewOptions(reviewId));
	const [selectedSection, setSelectedSection] = React.useState(SECTIONS[0].id);

	return (
		<>
			<div className="flex items-center gap-3 bg-white px-4 py-2">
				<button
					onClick={goBack}
					type="button"
					className="flex items-center gap-1.5 text-xs font-medium text-gray-500 transition-colors hover:text-primary-600"
				>
					<ArrowLeft size={13} />
					Back to list
				</button>
				<span className="text-gray-200">/</span>
				<span className="text-xs font-medium text-gray-700">Plan of Care Review</span>
				<span className="text-gray-200">/</span>
				<span className="text-xs font-medium text-primary-600">{review.data.patient.fullName}</span>
			</div>
			<div className="bg-white px-4 py-3">
				<div className="flex flex-wrap items-start justify-between gap-4">
					<div className="flex items-start gap-4">
						<div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-100">
							<User size={18} className="text-primary-600" />
						</div>
						<div>
							<div className="flex flex-wrap items-center gap-2">
								<h2 className="text-sm font-semibold text-gray-900">{review.data.patient.fullName}</h2>
								<span className="text-xs text-gray-400">{review.data.patient.patientIdentifier}</span>
								{/* {review.data.planOfCare.primaryDisciplineId && (
									<DisciplineChip disciplineId={review.data.planOfCare.primaryDisciplineId} />
								)} */}
								<StatusBadge {...review} />
								<PriorityBadge {...review} />
							</div>
							<div className="mt-1.5 flex flex-wrap items-center gap-4">
								<span className="flex items-center gap-1 text-xs text-gray-500">
									<Calendar size={11} />
									DOB: {review.data.patient.dateOfBirth.toLocaleDateString()}
								</span>
								<span className="flex items-center gap-1 text-xs text-gray-500">
									<MapPin size={11} />
									{review.data.address.visitAddress.addressOne}
								</span>
								<span className="flex items-center gap-1 text-xs text-gray-500">
									<Stethoscope size={11} />
									{review.data.caseManager ? `Case Manager: ${review.data.caseManager.fullName}` : 'No Case Manager'}
								</span>
								<span className="flex items-center gap-1 text-xs text-gray-500">
									<FileText size={11} />
									{review.data.certificationPhysician
										? `Dr. ${review.data.certificationPhysician.physician.fullName}`
										: 'No Certification Physician'}
								</span>
							</div>
						</div>
					</div>
					<div className="flex shrink-0 items-center gap-5 text-right">
						<div>
							<p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">SOC Date</p>
							<p className="mt-0.5 text-xs text-gray-700">{review.data.admission.startOfCareDate.toLocaleDateString()}</p>
						</div>
						<div>
							<p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Episode End</p>
							<p className="mt-0.5 text-xs text-gray-700">{review.signature.certificationPeriod.endDate.toLocaleDateString()}</p>
						</div>
						<div>
							<p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Review Deadline</p>
							<div className="mt-0.5">
								<DeadlineText dueAt={review.dueAt} />
							</div>
						</div>
						<div>
							<p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Payer</p>
							<p className="mt-0.5 text-xs text-gray-700">
								{review.data.insurance.payors.find(x => x.order === 1)!.insuranceProviderName}
							</p>
						</div>
					</div>
				</div>
			</div>
			<Tabs selectedKey={selectedSection} onSelectionChange={setSelectedSection} className="rounded-t-none border-none">
				<Tabs.TabList className="rounded-t-none">
					{SECTIONS.map(tab => (
						<Tabs.Tab key={tab.id} id={tab.id}>
							{tab.label}
						</Tabs.Tab>
					))}
				</Tabs.TabList>
				<Tabs.TabPanels>
					<SuspenseWrapper name="planOfCareReview">
						<Tabs.TabPanel id="referral">
							<ReferralTab review={review} />
						</Tabs.TabPanel>
						<Tabs.TabPanel id="demographics">
							<DemographicsTab review={review} />
						</Tabs.TabPanel>
						<Tabs.TabPanel id="orders">
							<OrdersTab review={review} />
						</Tabs.TabPanel>
						<Tabs.TabPanel id="oasis">
							{review.oasisAssessment ? (
								<OasisTab oasisAssessment={review.oasisAssessment} />
							) : (
								<Callout title="No OASIS assessment" subtitle="This certification period does not have an OASIS assessment yet." />
							)}
						</Tabs.TabPanel>
						<Tabs.TabPanel id="dx">
							<DiagnosesTab review={review} />
						</Tabs.TabPanel>
						<Tabs.TabPanel id="locator-items">
							<LocatorItemsTab review={review} />
						</Tabs.TabPanel>
						<Tabs.TabPanel id="discharge-plans">
							<PlanOfCareDischargePlans signatureId={review.signature.id} />
						</Tabs.TabPanel>
						<Tabs.TabPanel id="discharge-plans">
							<ReviewTab review={review} />
						</Tabs.TabPanel>
					</SuspenseWrapper>
				</Tabs.TabPanels>
			</Tabs>
		</>
	);
}
